import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { reportError } from '@/lib/reportError';

/**
 * useSessionReady — returns true only when the Supabase session is
 * genuinely ready for authenticated DB writes, not just when useAuth's
 * isAuthenticated flag flipped.
 *
 * Why this exists (H-103):
 *
 * Supabase's onAuthStateChange fires INITIAL_SESSION / SIGNED_IN as soon
 * as the JWT is *present*, but there's a short window (especially on
 * Safari iOS with ITP, and immediately after an OAuth redirect) where
 * the token hasn't yet propagated to RLS evaluation. If the app does an
 * authenticated INSERT in that window, the row is silently rejected by
 * RLS, the client sees success, and the save-after-login toast lies.
 *
 * The CTO audit documented this at P0.3. The v2 fix was described as
 * "gate the pending save on an explicit session-ready signal". This is
 * that signal.
 *
 * Strategy:
 * - When the user/session changes to a signed-in value, issue a trivial
 *   SELECT against user_settings (an RLS-protected table) and watch
 *   whether the result comes back successfully.
 * - Retry up to MAX_ATTEMPTS with ATTEMPT_DELAY_MS between attempts to
 *   cover Safari ITP / OAuth propagation delays (usually 1-2 seconds).
 * - On persistent failure, `ready` stays false and `error` is populated.
 *   Callers (the pending-save effect) should abort gracefully and show
 *   the user a failure toast rather than fire a write that might lose
 *   data.
 * - When the user signs out, `ready` resets to false immediately.
 *
 * This hook does NOT replace useAuth — callers still need useAuth for
 * the user object. It's a narrower, stricter readiness signal on top.
 */

const MAX_ATTEMPTS = 5;
const ATTEMPT_DELAY_MS = 500;

export function useSessionReady(userId: string | null) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attemptRef = useRef(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    attemptRef.current = 0;
    setReady(false);
    setError(null);

    if (!userId || !supabase) {
      return () => {
        cancelledRef.current = true;
      };
    }

    // Capture the non-null client into a const so TS can narrow across
    // the async closures below.
    const client = supabase;

    const attempt = async () => {
      if (cancelledRef.current) return;
      attemptRef.current += 1;

      try {
        const { error: selectError } = await client
          .from('user_settings')
          .select('user_id')
          .eq('user_id', userId)
          .maybeSingle();

        if (cancelledRef.current) return;

        if (!selectError) {
          // The SELECT returned (possibly with no row — that's fine,
          // user_settings may not exist yet for a brand-new user). The
          // point is that the token was accepted by RLS.
          setReady(true);
          return;
        }

        // Non-null error — if we have attempts left, retry.
        if (attemptRef.current < MAX_ATTEMPTS) {
          setTimeout(attempt, ATTEMPT_DELAY_MS);
          return;
        }

        // Out of retries.
        const msg = selectError.message || 'Session readiness check failed';
        reportError('sessionReady.exhausted', selectError, {
          userId,
          attempts: attemptRef.current,
        });
        if (!cancelledRef.current) {
          setError(msg);
        }
      } catch (err) {
        if (cancelledRef.current) return;
        if (attemptRef.current < MAX_ATTEMPTS) {
          setTimeout(attempt, ATTEMPT_DELAY_MS);
          return;
        }
        reportError('sessionReady.threw', err, {
          userId,
          attempts: attemptRef.current,
        });
        setError(err instanceof Error ? err.message : 'Session readiness check threw');
      }
    };

    attempt();

    return () => {
      cancelledRef.current = true;
    };
  }, [userId]);

  return { ready, error };
}
